import React from "react";
import {
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Checkbox,
  Box,
  Typography,
  useTheme,
  IconButton,
  Menu,
  MenuItem,
  Collapse,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import {
  DndContext,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  closestCenter,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { DtListItem, DtListAction, getDefaultIcon } from "./DtList";

// Tree type: allow nested children with item-level drag control
export type TreeItem = DtListItem & {
  children?: TreeItem[];
  draggable?: boolean; // ✅ Item-level drag control
};

export interface DtArrangebleListProps {
  items: TreeItem[];
  selectable?: boolean;
  onSelect?: (id: string | number, checked: boolean) => void;
  dense?: boolean;
  sx?: Record<string, unknown>;
  listItemSx?:
    | Record<string, unknown>
    | ((item: TreeItem, idx: number) => Record<string, unknown>);
  onArrange?: (event: { parent: string | null; arrangedItems: { item: string | number; Order: number }[] }) => void;
  collapsibleChildren?: boolean;
  defaultExpanded?: boolean;
  onClick?: (item: TreeItem) => void;
  syncOnChange?: boolean;
  dndSx?: Record<string, unknown>;
}

interface DtListState<T extends TreeItem = TreeItem> {
  menuAnchor: HTMLElement | null;
  menuItemIdx: number | null;
  dragItems: T[];
}

interface SortableRowProps {
  item: TreeItem;
  idx: number;
  dense: boolean;
  selectable: boolean;
  customSx: Record<string, unknown>;
  actions: DtListAction[];
  useDropdown: boolean;
  theme: import("@mui/material/styles").Theme;
  isMenuOpen: boolean;
  menuAnchor: HTMLElement | null;
  onOpenMenu: (e: React.MouseEvent<HTMLElement>, idx: number) => void;
  onCloseMenu: () => void;
  onSelect?: (id: string | number, checked: boolean) => void;
  onRowClick?: () => void;
  endAdornment?: React.ReactNode;
  onClick?: (item: TreeItem) => void;
}

const SortableRow: React.FC<SortableRowProps> = ({
  item,
  idx,
  dense,
  selectable,
  customSx,
  actions,
  useDropdown,
  theme,
  isMenuOpen,
  menuAnchor,
  onOpenMenu,
  onCloseMenu,
  onSelect,
  onRowClick,
  onClick,
  endAdornment,
}) => {
  const isDraggable = item.draggable !== false;

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: String(item.id),
    disabled: !isDraggable,
  });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <ListItem
      ref={setNodeRef}
      {...(isDraggable ? { ...attributes, ...listeners } : {})} 
      onClick={() => {
        onRowClick?.();
        onClick?.(item);
      }}
      dense={dense}
      disableGutters
      sx={{
        ...style,
        borderRadius: 2,
        mb: 0.5,
        px: 1.5,
        py: 0.5,
        color: item.disabled ? theme.palette.text.disabled : theme.palette.text.primary,
        cursor: isDraggable ? "grab" : "pointer",
        background: item.checked
          ? `${theme.palette.info.light}20`
          : isDragging
          ? `${theme.palette.info.light}15`
          : undefined,
        "&:hover": {
          background: !item.checked ? `${theme.palette.info.light}10` : undefined,
        },
        opacity: item.disabled ? 0.6 : 1,
        transition: "background 0.2s",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        ...customSx,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", flex: 1 }}>
        {selectable && (
          <ListItemIcon sx={{ minWidth: 32 }} onClick={(e) => e.stopPropagation()}>
            <Checkbox
              edge="start"
              checked={!!item.checked}
              disabled={item.disabled}
              tabIndex={-1}
              onChange={(e) => onSelect?.(item.id, e.target.checked)}
              sx={{
                color: theme.palette.primary.main,
                "&.Mui-checked": { color: theme.palette.primary.main },
              }}
            />
          </ListItemIcon>
        )}
        {item.content ? (
          item.content
        ) : (
          <ListItemText
            primary={
              <Typography
                sx={{
                  fontWeight: item.checked ? 600 : 400,
                  color: item.disabled ? theme.palette.text.disabled : theme.palette.text.primary,
                }}
              >
                {item.label}
              </Typography>
            }
            secondary={item.secondary}
          />
        )}
      </Box>

      {/* Optional right-side actions */}
      {actions.length > 0 &&
        (useDropdown ? (
          <>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onOpenMenu(e, idx);
              }}
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
            <Menu
              anchorEl={menuAnchor}
              open={isMenuOpen}
              onClose={onCloseMenu}
              anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
              transformOrigin={{ vertical: "top", horizontal: "center" }}
            >
              {actions.map((action,index) => (
                <MenuItem
                  key={`${String(item.id)}-${action.type}-${index}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    onCloseMenu();
                    action.onClick?.(item, idx);
                  }}
                >
                  <ListItemIcon>{action.icon ?? getDefaultIcon(action.type)}</ListItemIcon>
                  <ListItemText>{action.label ?? action.type}</ListItemText>
                </MenuItem>
              ))}
            </Menu>
          </>
        ) : (
          <Box
            sx={{ ml: 1, display: "flex", alignItems: "center", gap: 0.5 }}
            onClick={(e) => e.stopPropagation()}
          >
            {actions.map((action, index) => (
              <IconButton
                key={`${String(item.id)}-${action.type}-${index}`}
                size="small"
                aria-label={action.type}
                onClick={(e) => {
                  e.stopPropagation();
                  action.onClick?.(item, idx);
                }}
              >
                {action.icon ?? getDefaultIcon(action.type)}
              </IconButton>
            ))}
          </Box>
        ))}

      {/* Right adornment (expand/collapse button) */}
      {endAdornment}
    </ListItem>
  );
};

const DtArrangebleList: React.FC<DtArrangebleListProps> = ({
  items,
  selectable = false,
  onSelect,
  dense = false,
  sx,
  listItemSx,
  onArrange,
  onClick,
  collapsibleChildren = false,
  defaultExpanded = true,
  syncOnChange = true,
  dndSx,
}) => {
  const theme = useTheme();
  const [state, setState] = React.useState<DtListState>({
    menuAnchor: null,
    menuItemIdx: null,
    dragItems: items,
  });

  // ✅ Only sync dragItems with prop changes if syncOnChange is true
  React.useEffect(() => {
    if (syncOnChange) {
      setState((prev) => ({ ...prev, dragItems: items }));
    }
  }, [items, syncOnChange]);

  const [expandedMap, setExpandedMap] = React.useState<Record<string, boolean>>({});
  const [draggedId, setDraggedId] = React.useState<string | null>(null);
  const [prevExpanded, setPrevExpanded] = React.useState<boolean | null>(null);

  React.useEffect(() => {
    setExpandedMap((prev) => {
      if (Object.keys(prev).length === 0) {
        const initialMap: Record<string, boolean> = {};
        items.forEach((item) => {
          initialMap[item.id] = defaultExpanded; // Use defaultExpanded prop
        });
        return initialMap;
      }
      return prev;
    });
  }, [items, defaultExpanded]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, idx: number): void => {
    event.stopPropagation();
    setState((prev) => ({ ...prev, menuAnchor: event.currentTarget, menuItemIdx: idx }));
  };

  const handleMenuClose = (): void => {
    setState((prev) => ({ ...prev, menuAnchor: null, menuItemIdx: null }));
  };

  const handleDragStart = (event: any) => {
    if (!collapsibleChildren) return;
    const id = String(event.active.id);
    const item = state.dragItems.find((i) => String(i.id) === id);
    const hasChildren = item && Array.isArray(item.children) && item.children.length > 0;
    if (hasChildren) {
      setDraggedId(id);
      setPrevExpanded(expandedMap[id]);
      setExpandedMap((prev) => ({ ...prev, [id]: false }));
    }
  };

  const handleDragEnd = (event: DragEndEvent): void => {
    const { active, over } = event;
    if (!over || active.id === over.id) {
      if (draggedId && prevExpanded !== null) {
        setExpandedMap((prev) => ({ ...prev, [draggedId]: prevExpanded }));
        setDraggedId(null);
        setPrevExpanded(null);
      }
      return;
    }

    const draggedItem = state.dragItems.find((i) => String(i.id) === String(active.id));
    if (!draggedItem || draggedItem.draggable === false) {
      return;
    }

    const oldIndex = state.dragItems.findIndex((i) => String(i.id) === String(active.id));
    const newIndex = state.dragItems.findIndex((i) => String(i.id) === String(over.id));
    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = arrayMove(state.dragItems, oldIndex, newIndex);

    // Assign sequential order starting from 0
    let order = 0;
    const orderedItems = reordered.map((item) => ({
      ...item,
      Order: order++,
    }));

    setState((prev) => ({ ...prev, dragItems: orderedItems }));

    // Pass event in requested format
    const arrangeEvent = flattenArranged(orderedItems, null);
    onArrange?.(arrangeEvent);

    if (draggedId && prevExpanded !== null) {
      setExpandedMap((prev) => ({ ...prev, [draggedId]: prevExpanded }));
      setDraggedId(null);
      setPrevExpanded(null);
    }
  };

  const toggleExpand = (id: string | number) => {
    setExpandedMap((prev) => ({
      ...prev,
      [String(id)]: !prev[String(id)],
    }));
  };

  function sortByOrder(items: TreeItem[]): TreeItem[] {
    return [...items]
      .sort((a, b) => (a.Order ?? 0) - (b.Order ?? 0))
      .map((item) => ({
        ...item,
        children: item.children ? sortByOrder(item.children) : undefined,
      }));
  }

  function flattenArranged(
    items: TreeItem[],
    parent: string | null = null
  ): { parent: string | null; arrangedItems: { item: string | number; Order: number }[] } {
    return {
      parent,
      arrangedItems: items.map((i) => ({ item: i.id, Order: i.Order ?? 0 })),
    };
  }

  const itemsToRender = draggedId ? state.dragItems : items;
  const draggableItems = itemsToRender.filter((item) => item.draggable !== false);

  const listBody = (
    <List sx={{ width: "100%", bgcolor: "background.paper", borderRadius: 2, ...sx }}>
      {sortByOrder(itemsToRender).map((item, idx) => {
        const actionConfig = item.actionConfig ?? { actions: [] };
        const actions = Array.isArray(actionConfig.actions) ? actionConfig.actions : [];
        const useDropdown = actionConfig.mode === "dropdown" || actions.length > 3;

        const customSx =
          typeof listItemSx === "function" ? listItemSx(item, idx) : (listItemSx || {});

        const hasChildren = Array.isArray(item.children) && item.children.length > 0;
        const expanded = expandedMap[String(item.id)] ?? defaultExpanded; // use defaultExpanded as fallback

        const endAdornment = collapsibleChildren && hasChildren ? (
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              toggleExpand(item.id);
            }}
            onMouseDown={(e) => e.stopPropagation()}
            sx={{ ml: 0.5 }}
            aria-label={expanded ? "Collapse" : "Expand"}
          >
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        ) : null;

        return (
          <React.Fragment key={String(item.id)}>
            <SortableRow
              item={item}
              idx={idx}
              dense={!!dense}
              selectable={!!selectable}
              customSx={customSx}
              actions={actions}
              useDropdown={useDropdown}
              theme={theme}
              isMenuOpen={state.menuItemIdx === idx && Boolean(state.menuAnchor)}
              menuAnchor={state.menuAnchor}
              onOpenMenu={handleMenuOpen}
              onCloseMenu={handleMenuClose}
              onSelect={onSelect}
              onRowClick={() => {
                if (selectable && onSelect) onSelect(item.id, !item.checked);

                // Collapse all parent items (items with children)
                setExpandedMap((prev) => {
                  const newMap = { ...prev };
                  state.dragItems.forEach((parent) => {
                    if (parent.children && parent.children.length > 0) {
                      newMap[String(parent.id)] = false;
                    }
                  });
                  return newMap;
                });
              }}
              onClick={onClick}
              endAdornment={endAdornment}
            />

            {hasChildren &&
              (collapsibleChildren ? (
                <Collapse in={expanded} timeout="auto" unmountOnExit>
                  <Box sx={{ ml: 3, mt: 0.5 }}>
                    <DtArrangebleList
                      items={sortByOrder(item.children ?? [])}
                      selectable={selectable}
                      dense={dense}
                      onSelect={onSelect}
                      listItemSx={listItemSx}
                      onArrange={(event) => {
                        // Pass parent name for children
                        if (onArrange) {
                          onArrange({
                            parent: item.label?.toString() ?? null,
                            arrangedItems: event.arrangedItems,
                          });
                        }
                      }}
                      onClick={onClick}
                      collapsibleChildren={collapsibleChildren}
                      defaultExpanded={defaultExpanded}
                    />
                  </Box>
                </Collapse>
              ) : (
                <Box sx={{ ml: 3, mt: 0.5 }}>
                  <DtArrangebleList
                    items={sortByOrder(item.children ?? [])}
                    selectable={selectable}
                    dense={dense}
                    onSelect={onSelect}
                    listItemSx={listItemSx}
                    onArrange={(event) => {
                      if (onArrange) {
                        onArrange({
                          parent: item.label?.toString() ?? null,
                          arrangedItems: event.arrangedItems,
                        });
                      }
                    }}
                    onClick={onClick}
                    collapsibleChildren={collapsibleChildren}
                    defaultExpanded={defaultExpanded}
                  />
                </Box>
              ))}
          </React.Fragment>
        );
      })}
    </List>
  );

  if (draggableItems.length === 0) {
    // Nothing draggable (or data not yet ready) – render static list but keep expand/collapse working
    return listBody;
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <Box sx={dndSx}>
        <SortableContext items={draggableItems.map((i) => String(i.id))} strategy={verticalListSortingStrategy}>
          {listBody}
        </SortableContext>
      </Box>
    </DndContext>
  );
};

export { DtArrangebleList };
